import { httpClient } from '../client';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';
import {
  ChatLogBooking,
  ChatSearchResponse,
  ChatType,
  PaginatedChatLogBookingsData,
  PaginatedChatLogBookingsResponse,
  SingleChatLogBookingResponse,
  UpdateChatLogBookingDTO,
} from '../../../types/chat_booking';

class ChatBookingEndpoints extends BaseEndpoint {
  private static instance: ChatBookingEndpoints;

  private constructor() {
    super('chat');
  }

  static getInstance(): ChatBookingEndpoints {
    if (!ChatBookingEndpoints.instance) {
      ChatBookingEndpoints.instance = new ChatBookingEndpoints();
    }
    return ChatBookingEndpoints.instance;
  }

  async getChatLogBookingById(
    organizationId: string | null,
    chatLogId: string
  ): Promise<ChatLogBooking | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<SingleChatLogBookingResponse>(
        `${this.basePath}/${organizationId}/chat-logs/${chatLogId}`,
        { headers }
      );
      logger.info(`Chat log ${chatLogId} fetched successfully`);
      return response.data.data;
    } catch (error) {
      logger.error(`Failed to fetch chat log ${chatLogId}:`, error);
      return null;
    }
  }

  async getChatLogBookings(
    organizationId: string | null,
    page: number = 1,
    pageSize: number = 5,
    chatType: ChatType | null = null,
    isVoice?: boolean
  ): Promise<PaginatedChatLogBookingsData | null> {
    try {
      const headers = await this.getAuthHeaders();
      let url = `${this.basePath}/${organizationId}/chat-logs?page=${page}&pageSize=${pageSize}`;
      if (chatType) url += `&chatType=${chatType}`;
      if (isVoice !== undefined) url += `&isVoice=${isVoice}`;
      const response = await httpClient.get<PaginatedChatLogBookingsResponse>(url, { headers });

      logger.info('Chat logs fetched successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch chat logs:', error);
      return null;
    }
  }

  async getChatLogBookingsBetweenDates(
    organizationId: string | null,
    startDate: string,
    endDate: string,
    page: number = 1,
    pageSize: number = 10,
    chatType: ChatType | null = null
  ): Promise<PaginatedChatLogBookingsData | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get(
        `${this.basePath}/${organizationId}/chat-logs` +
          `?page=${page}` +
          `&pageSize=${pageSize}` +
          `&fromDate=${startDate}` +
          `&toDate=${endDate}` +
          (chatType ? `&chatType=${chatType}` : ''),
        { headers }
      );

      logger.info('Chat logs between dates fetched successfully');
      return response.data.data as PaginatedChatLogBookingsData;
    } catch (error) {
      logger.error('Failed to fetch chat logs between dates:', error);
      return null;
    }
  }

  async updateChatLogBookingViewedStatus(
    organizationId: string | null,
    chatLogId: string,
    data: UpdateChatLogBookingDTO
  ): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.patch(`${this.basePath}/${organizationId}/chat-logs/${chatLogId}`, data, {
        headers,
      });
      logger.info(`Chat log ${chatLogId} viewed status updated successfully`);
    } catch (error) {
      logger.error(`Failed to update chat log ${chatLogId} viewed status:`, error);
    }
  }
  async searchChatLogs(
    organizationId: string | null,
    searchTerm: string,
    startDate: string,
    endDate: string
  ): Promise<ChatSearchResponse | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<ChatSearchResponse>(
        `${this.basePath}/${organizationId}/chat-logs/search`,
        {
          params: {
            fromDate: startDate,
            toDate: endDate,
            search: searchTerm,
          },
          headers,
        }
      );
      logger.info(`Search for "${searchTerm}" completed`);
      return response.data;
    } catch (error) {
      logger.error(`Failed to search chat logs:`, error);
      return null;
    }
  }
}

export const chatBookingEndpoints = ChatBookingEndpoints.getInstance();
